// src/components/paginas/RutasDashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';

/* ===== helpers consistentes con el resto ===== */
const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

const fetchJSON = async (url, opts = {}) => {
  const resp = await fetch(url, { credentials: 'include', ...opts });
  let j = null; try { j = await resp.json(); } catch {}
  if (!resp.ok || (j && j.ok === false)) {
    const msg = (j && j.msg) || resp.statusText || `HTTP ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status; err.payload = j;
    throw err;
  }
  return j ?? {};
};

function b64toBlob(b64, type = '') {
  // soporta base64 largos de forma segura
  const sliceSize = 1024;
  const byteChars = atob(b64);
  const bytesLength = byteChars.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);
  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);
    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteChars.charCodeAt(offset);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type });
}
/* ============================================= */

export default function RutasDashboard() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [capital, setCapital] = useState([]);
  const [interior, setInterior] = useState([]);
  const [sel, setSel] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const j = await fetchJSON(`${API_BASE}/api/rutas/dashboard`);
      setCapital(j.capital || []);
      setInterior(j.interior || []);
      setSel(new Set());
    } catch (e) {
      setErr(e.message || 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSel(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = (arr, on) => setSel(prev => {
    const n = new Set(prev);
    arr.forEach(x => { on ? n.add(x.id) : n.delete(x.id); });
    return n;
  });

  const genPdf = async (group, ids) => {
    if (!ids.length) return;
    const url = group === 'capital'
      ? `${API_BASE}/api/rutas/pdf/capital`
      : `${API_BASE}/api/rutas/pdf/interior`;
    try {
      const j = await fetchJSON(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!j.pdfBase64) throw new Error('Respuesta sin PDF');
      const blob = b64toBlob(j.pdfBase64, 'application/pdf');
      const urlObj = URL.createObjectURL(blob);
      window.open(urlObj, '_blank');
    } catch (e) {
      alert(`Error: ${e.message || e}`);
    }
  };

  // seleccionados por grupo
  const selCapital  = useMemo(() => capital.filter(x => sel.has(x.id)).map(x => x.id), [capital, sel]);
  const selInterior = useMemo(() => interior.filter(x => sel.has(x.id)).map(x => x.id), [interior, sel]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold">Rutas — Pedidos en Ruta</h1>
      <div className="text-sm text-slate-600 mt-1">Selecciona direcciones y genera la hoja de ruta por grupo.</div>

      <div className="mt-4 flex gap-3">
        <button className="px-4 py-2 rounded-lg border" onClick={load} disabled={loading}>
          {loading ? 'Actualizando…' : 'Actualizar'}
        </button>
      </div>

      {err && <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded">{err}</div>}

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Section
          title={`Capital y Municipios Aledaños (${capital.length})`}
          data={capital}
          sel={sel}
          onToggle={toggle}
          onToggleAll={(on) => toggleAll(capital, on)}
          onGenerate={() => genPdf('capital', selCapital)}
          generateDisabled={!selCapital.length}
          actionText={`Generar PDF (${selCapital.length})`}
        />
        <Section
          title={`Interior del País (${interior.length})`}
          data={interior}
          sel={sel}
          onToggle={toggle}
          onToggleAll={(on) => toggleAll(interior, on)}
          onGenerate={() => genPdf('interior', selInterior)}
          generateDisabled={!selInterior.length}
          actionText={`Generar PDF (${selInterior.length})`}
        />
      </div>
    </div>
  );
}

function Section({ title, data, sel, onToggle, onToggleAll, onGenerate, generateDisabled, actionText }) {
  return (
    <div className="border rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold">{title}</h2>
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1 rounded border" onClick={() => onToggleAll(true)}>Seleccionar todo</button>
          <button className="px-3 py-1 rounded border" onClick={() => onToggleAll(false)}>Quitar todo</button>
          <button
            className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
            onClick={onGenerate}
            disabled={generateDisabled}
          >
            {actionText}
          </button>
        </div>
      </div>

      <div className="mt-3 divide-y">
        {!data.length && <div className="py-4 text-slate-500">— Sin elementos —</div>}
        {data.map(p => (
          <label key={p.id} className="py-3 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={sel.has(p.id)} onChange={() => onToggle(p.id)} className="mt-1"/>
            <div className="flex-1">
              <div className="font-semibold">
                #{p.id} — {p.nombreCliente || '—'}{' '}
                <span className="text-xs text-slate-500">({p.whatsapp || '—'})</span>
              </div>
              <div className="text-sm text-slate-700">{p.direccionEntrega || '—'}</div>
              <div className="text-xs text-slate-500">
                Boleta: {p.boleta || '—'} · Banco: {p.banco || '—'} · Total: Q{Number(p.total || 0).toFixed(2)} · NIT: {p.nit || '—'}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
